import { toaster } from '@/components/ui/toaster';
import {
  INVITATION_OUTREACH_LEXICAL_VARIABLES,
  LEXICAL_VARIABLES,
  MAX_BODY_LENGTH,
  WARN_BODY_LENGTH,
} from '@/components/affiliate/bulk-email/editor-config';
import { useSendBulkEmail } from '@/hooks/actions/use-send-bulk-email';
import { useSendGiftBulkEmail } from '@/hooks/actions/use-send-gift-bulk-email';
import { useSendBulkCreatorOutreachEmail } from '@/hooks/actions/use-send-bulk-creator-outreach-email';
import { useSendBulkInvitationOutreachEmail } from '@/hooks/actions/use-send-bulk-invitation-outreach-email';
import { useSendCreatorOutreachEmail } from '@/hooks/actions/use-send-creator-outreach-email';
import { useSendCreatorContactOutreachEmail } from '@/hooks/actions/use-send-creator-contact-outreach-email';
import { useTrackedInvitationOutreach } from '@/hooks/actions/use-tracked-invitation-outreach';
import { useEmailAttachments } from '@/hooks/use-email-attachments';
import { useIntegrationsQuery } from '@/hooks/queries/use-integrations-query';
import { getConnectedEmailIntegration } from '@/hooks/use-email-integration-status';
import useAffiliateBulkEmailStore, {
  getIncompatibleRecipients,
} from '@/stores/affiliate-bulk-email-store';
import useBulkEmailProgressStore from '@/stores/bulk-email-progress-store';
import { plainTextToEmailHtml, plainTextToLexicalJson } from '@/libs/utils/lexical-utils';
import { creatorSpaceUrl } from '@/libs/creator-space-url';
import { bulkEmailPusherService } from '@/services/bulk-email-pusher-service';
import { creatorOutreachPusherService } from '@/services/creator-outreach-pusher-service';
import { useAuth } from '@/contexts/auth-context';
import type {
  BulkAffiliateEmailFilters,
  BulkEmailSendRequest,
  BulkInvitationOutreachEmailResult,
  BulkWishlistOutreachResultLine,
  TemplateLibraryLanguage,
} from '@fluenceur/types';
import {
  BULK_AFFILIATE_EMAIL_LIMIT,
  BULK_INVITATION_OUTREACH_LIMIT,
  PROGRAM_SCOPED_VARIABLES,
  countMessageCharacters,
} from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';
import { useQueryClient } from '@tanstack/react-query';
import Mustache from 'mustache';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { emailComposerModeToPair } from '@/components/affiliate/bulk-email/email-composer-template-pair';
import {
  useEmailComposerTemplateFlow,
  type EmailEditorSnapshot,
} from '@/components/affiliate/bulk-email/use-email-composer-template-flow';
import type { EmailComposerMode } from './use-email-composer-mode';

export type { EmailComposerMode };

type Step = 'compose' | 'confirm';

interface UseEmailComposerOptions {
  /** When true, include program-scoped variables */
  hasProgramContext: boolean;
  mode?: EmailComposerMode;
  /**
   * Filter to resolve recipients server-side when the affiliate selection is in
   * "select all matching" mode. Supplied by the table (its current filters).
   */
  selectAllMatchingFilters?: BulkAffiliateEmailFilters;
  /**
   * A message the surface pre-fills, read once when the composer mounts. The
   * brand edits it from there; nothing re-seeds it while it is open.
   */
  initialDraft?: { subject: string; body: string } | null;
  /** Called after successful send and state reset */
  onClose: () => void;
}

const CREATOR_OUTREACH_VARIABLE_KEYS = ['first_name', 'brand_name'] as const;
// A plain email to an invitation: the generic outreach variables plus the
// invitation context every invitation carries (its program, its link, the
// benefit it offers) and the sender, which the Fluenceur templates sign with.
const INVITATION_PLAIN_OUTREACH_VARIABLE_KEYS = [
  ...CREATOR_OUTREACH_VARIABLE_KEYS,
  'program_name',
  'invitation_link',
  'affiliate_benefit',
  'sender_name',
] as const;
const COMMISSION_RATE_VARIABLE_KEY = 'commission_rate';
const DEFAULT_EMAIL_COMPOSER_MODE: EmailComposerMode = { type: 'affiliate-bulk' };

export function useEmailComposer({
  hasProgramContext,
  mode = DEFAULT_EMAIL_COMPOSER_MODE,
  selectAllMatchingFilters,
  initialDraft,
  onClose,
}: UseEmailComposerOptions) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const sendBulkEmail = useSendBulkEmail();
  const sendGiftBulkEmail = useSendGiftBulkEmail();
  const sendBulkCreatorOutreachEmail = useSendBulkCreatorOutreachEmail();
  const sendBulkInvitationOutreachEmail = useSendBulkInvitationOutreachEmail();
  const sendCreatorOutreachEmail = useSendCreatorOutreachEmail();
  const sendCreatorContactOutreachEmail = useSendCreatorContactOutreachEmail();
  const trackedInvitationOutreach = useTrackedInvitationOutreach();

  const selectedIds = useAffiliateBulkEmailStore((s) => s.selectedIds);
  const recipients = useAffiliateBulkEmailStore((s) => s.recipients);
  const storeProgramId = useAffiliateBulkEmailStore((s) => s.programId);
  const selectAllMatching = useAffiliateBulkEmailStore((s) => s.selectAllMatching);
  const matchingTotal = useAffiliateBulkEmailStore((s) => s.matchingTotal);
  const { data: integrations } = useIntegrationsQuery();

  // Read once, on mount: a draft is a starting point, and re-applying it while
  // the brand is writing would take their words away mid-sentence.
  const seededDraft = useRef(initialDraft ?? null).current;

  const [step, setStep] = useState<Step>('compose');
  const [subject, setSubject] = useState(seededDraft?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(
    seededDraft ? plainTextToEmailHtml(seededDraft.body) : ''
  );
  const [bodyPlainText, setBodyPlainText] = useState(seededDraft?.body ?? '');
  const [resetKey, setResetKey] = useState(0);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isAwaitingCreatorOutreachResult, setIsAwaitingCreatorOutreachResult] = useState(false);
  // Per-recipient recap for the bulk creator-outreach mode: `null` while composing,
  // populated by the single bulk call's synchronous response so the surface can
  // swap the composer for a recap. No Pusher channel — the outcome is inline.
  const [bulkOutreachResults, setBulkOutreachResults] = useState<
    BulkWishlistOutreachResultLine[] | null
  >(null);
  // Grouped invitation outreach recap: `null` while composing, the queued count
  // and the skipped invitations once the single call returns.
  const [invitationOutreachBulkResult, setInvitationOutreachBulkResult] =
    useState<BulkInvitationOutreachEmailResult | null>(null);

  // Template wiring. `bodySeedKey` remounts the body editor (to re-seed an
  // applied template) independently of `resetKey`, which also resets the
  // subject — so applying a template never clears the subject.
  const [snapshot, setSnapshot] = useState<EmailEditorSnapshot | null>(null);
  const [initialContent, setInitialContent] = useState<string | null>(
    seededDraft ? plainTextToLexicalJson(seededDraft.body) : null
  );
  const [bodySeedKey, setBodySeedKey] = useState(0);

  const applyTemplateBody = useCallback((lexicalJson: string) => {
    setInitialContent(lexicalJson);
    setBodySeedKey((key) => key + 1);
  }, []);

  const queuedCreatorOutreachIdRef = useRef<string | null>(null);
  // The pusher service is a singleton: release only OUR registration so a
  // sibling surface subscribed to the same brand channel keeps its tracking.
  const creatorOutreachSubscriptionIdRef = useRef<string | null>(null);

  const releaseCreatorOutreachSubscription = useCallback(() => {
    if (creatorOutreachSubscriptionIdRef.current === null) return;
    creatorOutreachPusherService.unsubscribe(creatorOutreachSubscriptionIdRef.current);
    creatorOutreachSubscriptionIdRef.current = null;
  }, []);

  // Email integration
  const emailIntegration = useMemo(() => {
    return getConnectedEmailIntegration(integrations);
  }, [integrations]);

  const attachmentControls = useEmailAttachments({ provider: emailIntegration?.platform });
  const { attachments, setAttachments, deletingId, handleFileSelect, handleRemoveAttachment } =
    attachmentControls;

  const senderEmail =
    emailIntegration?.senderEmail ||
    (emailIntegration?.metadata as { email?: string } | undefined)?.email ||
    '';
  const isConnected = !!emailIntegration;
  // The name the send job signs `{{sender_name}}` with: the mailbox display name,
  // else the brand name.
  const senderDisplayName =
    (emailIntegration?.metadata as { name?: string } | undefined)?.name ||
    user?.currentBrand?.name ||
    '';
  const modeType = mode.type;
  const invitationKind =
    mode.type === 'invitation-outreach' || mode.type === 'invitation-outreach-bulk'
      ? mode.kind
      : null;
  const hasInvitationCommissionRate =
    mode.type === 'invitation-outreach' ? mode.hasCommissionRate : false;
  const hasInvitationLink = mode.type === 'invitation-outreach' ? mode.hasInvitationLink : false;
  const previewInviteeName =
    mode.type === 'invitation-outreach' ? mode.previewInviteeName : undefined;
  const previewProgramName =
    mode.type === 'invitation-outreach' ? mode.previewProgramName : undefined;

  // Variables
  const availableVariables: AvailableVariable[] = useMemo(() => {
    if (
      modeType === 'creator-outreach' ||
      modeType === 'creator-outreach-bulk' ||
      modeType === 'creator-contact-outreach'
    ) {
      return LEXICAL_VARIABLES.filter(
        (v) =>
          CREATOR_OUTREACH_VARIABLE_KEYS.includes(
            v.key as (typeof CREATOR_OUTREACH_VARIABLE_KEYS)[number]
          ) && !(PROGRAM_SCOPED_VARIABLES as readonly string[]).includes(v.key)
      );
    }

    if (invitationKind === 'outreach') {
      return INVITATION_PLAIN_OUTREACH_VARIABLE_KEYS.flatMap(
        (key) => INVITATION_OUTREACH_LEXICAL_VARIABLES.find((v) => v.key === key) ?? []
      );
    }

    // Every invitation of a grouped follow-up carries a live link (expired ones
    // are renewed), but not every program sets a commission rate.
    if (modeType === 'invitation-outreach-bulk') {
      return INVITATION_OUTREACH_LEXICAL_VARIABLES.filter(
        (v) => v.key !== COMMISSION_RATE_VARIABLE_KEY
      );
    }

    if (modeType === 'invitation-outreach') {
      return INVITATION_OUTREACH_LEXICAL_VARIABLES.filter(
        (v) =>
          (hasInvitationCommissionRate || v.key !== COMMISSION_RATE_VARIABLE_KEY) &&
          (hasInvitationLink || v.key !== 'invitation_link')
      );
    }

    if (hasProgramContext) return LEXICAL_VARIABLES;
    return LEXICAL_VARIABLES.filter(
      (v) => !(PROGRAM_SCOPED_VARIABLES as readonly string[]).includes(v.key)
    );
  }, [hasInvitationCommissionRate, hasInvitationLink, hasProgramContext, invitationKind, modeType]);

  // Template library: email/invitation for invitation outreach (carries a
  // link), email/generic otherwise. Scoped to the brand's send language.
  const templatePair = useMemo(() => emailComposerModeToPair(mode), [mode]);
  const templateLanguage = (user?.currentBrand?.settings?.preferredLanguage ??
    'en') as TemplateLibraryLanguage;
  const templateFlow = useEmailComposerTemplateFlow({
    brandId: user?.currentBrand?.id,
    pair: templatePair,
    language: templateLanguage,
    onApplyBody: applyTemplateBody,
    snapshot,
    attachments,
    onApplyAttachments: attachmentControls.replaceAttachments,
    attachmentsCustomized: attachmentControls.customized,
    attachmentsBlocked: attachmentControls.isSaveBlocked,
    attachmentsBusy: attachmentControls.isBusy,
  });

  const setBodyContent = useCallback((html: string, plainText = html) => {
    setBodyHtml(html);
    setBodyPlainText(plainText);
  }, []);

  // Validation
  const bodyLength = countMessageCharacters(bodyPlainText);
  const counterColor =
    bodyLength > MAX_BODY_LENGTH ? 'red' : bodyLength > WARN_BODY_LENGTH ? 'orange' : 'fg.muted';
  const hasIncompatibleRecipients = useMemo(() => {
    if (modeType !== 'affiliate-bulk') return false;
    // In "select all matching" mode we don't hold per-recipient data, so the
    // program-compatibility check (which inspects each recipient) doesn't apply.
    if (selectAllMatching) return false;
    return getIncompatibleRecipients(recipients, storeProgramId).length > 0;
  }, [modeType, recipients, storeProgramId, selectAllMatching]);
  // Recipient count surfaced to the UI: the whole filtered set when select-all
  // is on, otherwise the explicit id selection. A grouped invitation send
  // carries the count frozen with its selection.
  const recipientCount =
    mode.type === 'gift-bulk'
      ? recipients.size
      : mode.type === 'invitation-outreach-bulk'
        ? mode.recipientCount
        : mode.type === 'affiliate-bulk' && selectAllMatching
          ? matchingTotal
          : selectedIds.size;
  // Select-all mode is only sendable when the resolving filter is present;
  // otherwise the send would silently degrade to the current page's ids.
  const hasAffiliateBulkRecipients = selectAllMatching
    ? matchingTotal > 0 && !!selectAllMatchingFilters
    : selectedIds.size > 0;
  const hasRecipients =
    mode.type === 'gift-bulk'
      ? mode.giftIds.length > 0 && recipients.size > 0
      : mode.type === 'creator-outreach'
        ? mode.wishlistItemId.length > 0
        : mode.type === 'creator-outreach-bulk'
          ? mode.wishlistItemIds.length > 0
          : mode.type === 'creator-contact-outreach'
            ? mode.creatorIdentityId.length > 0
            : mode.type === 'invitation-outreach'
              ? mode.invitationId.length > 0
              : mode.type === 'invitation-outreach-bulk'
                ? mode.recipientCount > 0
                : hasAffiliateBulkRecipients;
  const canProceed =
    hasRecipients &&
    (modeType !== 'gift-bulk' || recipientCount <= BULK_AFFILIATE_EMAIL_LIMIT) &&
    (modeType !== 'invitation-outreach-bulk' || recipientCount <= BULK_INVITATION_OUTREACH_LIMIT) &&
    subject.trim().length > 0 &&
    bodyPlainText.trim().length > 0 &&
    bodyLength <= MAX_BODY_LENGTH &&
    !hasIncompatibleRecipients &&
    !attachmentControls.isBlocked;

  // Preview
  const previewCompiled = useMemo(() => {
    if (!isPreviewing) return { subject: '', body: '' };
    try {
      const vars = {
        first_name: 'Jane',
        invitee_name: previewInviteeName || 'Jane Doe',
        brand_name: user?.currentBrand?.name || 'Your Brand',
        program_name: previewProgramName || 'Program',
        invitation_link: `${creatorSpaceUrl()}/invitation?token=example`,
        commission_rate: '15%',
        affiliate_benefit: t('invitationOutreach.preview.affiliateBenefit'),
        sender_name: senderDisplayName || 'Your Brand',
      };
      return {
        subject: Mustache.render(subject, vars),
        body: Mustache.render(bodyHtml, vars),
      };
    } catch {
      return { subject, body: bodyHtml };
    }
  }, [
    subject,
    bodyHtml,
    isPreviewing,
    previewInviteeName,
    previewProgramName,
    senderDisplayName,
    t,
    user?.currentBrand?.name,
  ]);

  const handleTogglePreview = useCallback(() => setIsPreviewing((prev) => !prev), []);

  useEffect(() => {
    if (modeType === 'creator-outreach') {
      return () => {
        queuedCreatorOutreachIdRef.current = null;
        releaseCreatorOutreachSubscription();
      };
    }
  }, [modeType, releaseCreatorOutreachSubscription]);

  // Send
  const handleSend = async () => {
    if (attachmentControls.isBlocked) return;
    const brandId = user?.currentBrand?.id;

    if (mode.type === 'creator-outreach') {
      queuedCreatorOutreachIdRef.current = null;
      let didSubscribeToCreatorOutreach = false;
      // A terminal event can land between the channel bind and the moment the
      // subscription id comes back: the handler then has no id to release.
      let didHandleTerminalOutreachEvent = false;
      const isCurrentOutreachEvent = (data: {
        outreachId: string;
        wishlistItemId: string | null;
      }) =>
        data.wishlistItemId === mode.wishlistItemId &&
        (!queuedCreatorOutreachIdRef.current ||
          data.outreachId === queuedCreatorOutreachIdRef.current);

      if (brandId) {
        try {
          const subscriptionId = await creatorOutreachPusherService.subscribe(brandId, {
            onComplete: (data) => {
              if (!isCurrentOutreachEvent(data)) return;
              didHandleTerminalOutreachEvent = true;

              queryClient.invalidateQueries({ queryKey: ['wishlist'] });
              toaster.create({
                title: t('creatorOutreach.events.complete.title'),
                description: t('creatorOutreach.events.complete.description'),
                type: 'success',
              });
              queuedCreatorOutreachIdRef.current = null;
              setIsAwaitingCreatorOutreachResult(false);
              releaseCreatorOutreachSubscription();
              resetAndClose();
            },
            onFailed: (data) => {
              if (!isCurrentOutreachEvent(data)) return;
              didHandleTerminalOutreachEvent = true;

              queryClient.invalidateQueries({ queryKey: ['wishlist'] });
              const translated = data.errorCode
                ? t(`errors.${data.errorCode}`, { defaultValue: '' })
                : '';
              toaster.create({
                title: t(
                  data.status === 'unknown'
                    ? 'bulkEmail.progress.unknown.title'
                    : 'creatorOutreach.events.failed.title'
                ),
                description:
                  translated || data.errorMessage || t('creatorOutreach.events.failed.description'),
                type: data.status === 'unknown' ? 'warning' : 'error',
                duration: 6000,
              });
              queuedCreatorOutreachIdRef.current = null;
              setIsAwaitingCreatorOutreachResult(false);
              releaseCreatorOutreachSubscription();
            },
          });
          creatorOutreachSubscriptionIdRef.current = subscriptionId;
          if (didHandleTerminalOutreachEvent) {
            releaseCreatorOutreachSubscription();
          }
          didSubscribeToCreatorOutreach = true;
        } catch (err) {
          console.warn('[EmailComposer] Creator outreach Pusher subscription failed:', err);
        }
      }

      sendCreatorOutreachEmail.mutate(
        {
          wishlistItemId: mode.wishlistItemId,
          subject: subject.trim(),
          body: bodyHtml.trim(),
          attachmentIds: attachments.map((a) => a.id),
        },
        {
          onSuccess: (data) => {
            if (didSubscribeToCreatorOutreach) {
              queuedCreatorOutreachIdRef.current = data.outreachId;
              setIsAwaitingCreatorOutreachResult(true);
              return;
            }

            toaster.create({
              title: t('creatorOutreach.send.queued.title'),
              description: t('creatorOutreach.send.queued.description'),
              type: 'success',
            });
            resetAndClose();
          },
          onError: () => {
            queuedCreatorOutreachIdRef.current = null;
            setIsAwaitingCreatorOutreachResult(false);
            releaseCreatorOutreachSubscription();
          },
        }
      );
      return;
    }

    if (mode.type === 'creator-outreach-bulk') {
      // One call for the whole selection. The response carries a per-recipient
      // recap, so on success we surface it in place instead of closing; the
      // surface swaps the composer for the recap and owns the final close.
      sendBulkCreatorOutreachEmail.mutate(
        {
          wishlistItemIds: mode.wishlistItemIds,
          subject: subject.trim(),
          body: bodyHtml.trim(),
          attachmentIds: attachments.map((a) => a.id),
        },
        {
          onSuccess: (data) => {
            setBulkOutreachResults(data.results);
          },
        }
      );
      return;
    }

    if (mode.type === 'gift-bulk') {
      if (!canProceed) return;
      // Reuse the global batch result surface. Subscription is best-effort:
      // a Pusher outage must never turn a confirmed email into a no-op.
      if (brandId) {
        try {
          const updateProgress = useBulkEmailProgressStore.getState().updateProgress;
          const completeBatch = useBulkEmailProgressStore.getState().completeBatch;
          await bulkEmailPusherService.subscribe(brandId, {
            onProgress: (data) => updateProgress(data.batchId, data.sent, data.failed),
            onComplete: (data) => {
              if (data.status === 'unknown' || (data.unknown ?? 0) > 0) {
                useBulkEmailProgressStore
                  .getState()
                  .markUnknown(data.batchId, data.sent, data.failed);
              } else completeBatch(data.batchId, data.sent, data.failed);
              bulkEmailPusherService.unsubscribe();
            },
          });
        } catch (error) {
          console.warn('[EmailComposer] Gift bulk Pusher subscription failed:', error);
        }
      }
      sendGiftBulkEmail.mutate(
        {
          giftIds: mode.giftIds,
          recipientUserIds: mode.recipientUserIds,
          idempotencyKey: mode.idempotencyKey,
          subject: subject.trim(),
          body: bodyHtml.trim(),
          attachmentIds: attachments.map((attachment) => attachment.id),
        },
        {
          onSuccess: (data) => {
            useBulkEmailProgressStore.getState().startBatch(data.batchId, data.totalCount);
            resetAndClose();
          },
          onError: () => bulkEmailPusherService.unsubscribe(),
        }
      );
      return;
    }

    if (mode.type === 'creator-contact-outreach') {
      // No Pusher completion channel for the contact-keyed path: the send hook
      // toasts "queued" on success and surfaces errors, so the composer only
      // resets and closes here.
      sendCreatorContactOutreachEmail.mutate(
        {
          creatorIdentityId: mode.creatorIdentityId,
          subject: subject.trim(),
          body: bodyHtml.trim(),
          attachmentIds: attachments.map((a) => a.id),
        },
        {
          onSuccess: () => resetAndClose(),
        }
      );
      return;
    }

    if (mode.type === 'invitation-outreach') {
      await trackedInvitationOutreach.send(
        {
          invitationId: mode.invitationId,
          subject: subject.trim(),
          body: bodyHtml.trim(),
          attachmentIds: attachments.map((a) => a.id),
          baseTemplateId: templateFlow.selectedTemplateId,
          kind: mode.kind,
        },
        {
          onComplete: resetAndClose,
          onQueued: resetAndClose,
        }
      );
      return;
    }

    if (mode.type === 'invitation-outreach-bulk') {
      if (!canProceed) return;
      // One call for the frozen selection. The response counts the queued
      // emails and names the skipped invitations, so on success the surface
      // swaps the composer for a recap, which owns the final close.
      sendBulkInvitationOutreachEmail.mutate(
        {
          ...mode.selection,
          subject: subject.trim(),
          body: bodyHtml.trim(),
          attachmentIds: attachments.map((a) => a.id),
          baseTemplateId: templateFlow.selectedTemplateId,
          kind: mode.kind,
        },
        {
          onSuccess: (data) => {
            setInvitationOutreachBulkResult(data);
          },
        }
      );
      return;
    }

    // Either an explicit id list, or a server-resolved filter for the whole set.
    const selection: Pick<BulkEmailSendRequest, 'affiliateUserIds' | 'filter'> =
      selectAllMatching && selectAllMatchingFilters
        ? { filter: selectAllMatchingFilters }
        : { affiliateUserIds: Array.from(selectedIds) };

    if (brandId) {
      try {
        const startBatch = useBulkEmailProgressStore.getState().startBatch;
        const updateProgress = useBulkEmailProgressStore.getState().updateProgress;
        const completeBatch = useBulkEmailProgressStore.getState().completeBatch;

        await bulkEmailPusherService.subscribe(brandId, {
          onProgress: (data) => updateProgress(data.batchId, data.sent, data.failed),
          onComplete: (data) => {
            if (data.status === 'unknown' || (data.unknown ?? 0) > 0) {
              useBulkEmailProgressStore
                .getState()
                .markUnknown(data.batchId, data.sent, data.failed);
            } else completeBatch(data.batchId, data.sent, data.failed);
            bulkEmailPusherService.unsubscribe();
          },
        });

        sendBulkEmail.mutate(
          {
            ...selection,
            ...(storeProgramId ? { programId: storeProgramId } : {}),
            subject: subject.trim(),
            body: bodyHtml.trim(),
            attachmentIds: attachments.map((a) => a.id),
          },
          {
            onSuccess: (data) => {
              startBatch(data.batchId, data.totalCount, () =>
                useAffiliateBulkEmailStore.getState().clearSelection()
              );
              resetAndClose();
            },
            onError: () => bulkEmailPusherService.unsubscribe(),
          }
        );
      } catch (err) {
        console.warn('[EmailComposer] Pusher subscription failed:', err);
      }
    }
  };

  const resetAndClose = () => {
    setStep('compose');
    setSubject('');
    setBodyHtml('');
    setBodyPlainText('');
    attachmentControls.resetAttachments();
    setIsPreviewing(false);
    setIsAwaitingCreatorOutreachResult(false);
    setBulkOutreachResults(null);
    setInvitationOutreachBulkResult(null);
    setResetKey((prev) => prev + 1);
    setSnapshot(null);
    setInitialContent(null);
    setBodySeedKey((prev) => prev + 1);
    templateFlow.resetSelection();
    templateFlow.closeSaveDrawer();
    onClose();
  };

  return {
    // State
    step,
    setStep,
    subject,
    setSubject,
    body: bodyHtml,
    bodyPlainText,
    setBody: setBodyContent,
    attachments,
    setAttachments,
    resetKey,
    /** The subject the body was seeded with, hydrating the subject row once. */
    initialSubject: seededDraft?.subject,
    deletingId,
    isPreviewing,
    selectedIds,
    storeProgramId,
    selectAllMatching,
    recipientCount,

    // Template wiring
    templateFlow,
    bodySeedKey,
    initialContent,
    onSnapshotChange: setSnapshot,

    // Derived
    isConnected,
    senderEmail,
    // Bulk creator-outreach recap: `null` while composing, per-recipient lines once sent.
    bulkOutreachResults,
    // Grouped invitation outreach recap: `null` while composing.
    invitationOutreachBulkResult,
    availableVariables,
    bodyLength,
    counterColor,
    canProceed,
    previewCompiled,

    // Handlers
    handleTogglePreview,
    handleFileSelect,
    handleRemoveAttachment,
    handleSend,
    handleClose: resetAndClose,

    // Mutation state
    isSending:
      mode.type === 'gift-bulk'
        ? sendGiftBulkEmail.isPending
        : mode.type === 'creator-outreach'
          ? sendCreatorOutreachEmail.isPending || isAwaitingCreatorOutreachResult
          : mode.type === 'creator-outreach-bulk'
            ? sendBulkCreatorOutreachEmail.isPending
            : mode.type === 'creator-contact-outreach'
              ? sendCreatorContactOutreachEmail.isPending
              : mode.type === 'invitation-outreach'
                ? trackedInvitationOutreach.isSending
                : mode.type === 'invitation-outreach-bulk'
                  ? sendBulkInvitationOutreachEmail.isPending
                  : sendBulkEmail.isPending,
    isUploading: attachmentControls.isUploading,
    attachmentControls,
  };
}
